<template>
  <div>
    <PageHeader title="จัดการผู้สอน" />

    <b-container fluid="xl" class="pt-5">
      <div class="inner bg-container">
        <div class="card card-csq no-shadow">
          <div class="card-header bg-light">
            <ul class="nav nav-tabs card-header-tabs float-left">
              <li class="nav-item">
                <a class="nav-link active" href="#">รายบุคคล</a>
              </li>
            </ul>
          </div>
          <div class="card-body">
            <b-form>
              <!-- ข้อมูลผู้สอน -->
              <div class="card border no-shadow mb-3 border-primary">
                <div class="card-header pt-3 border-0 bg-transparent">
                  <h5 class="card-title">ข้อมูลผู้สอน</h5>
                </div>
                <div class="card-body pb-0">
                  <div class="form-row">
                    <b-form-group class="col-md-6">
                      <label>ชื่อ (ภาษาไทย)</label>
                      <b-form-input
                        v-model="form.firstName"
                        type="text"
                        placeholder="ชื่อ"
                        :class="{ 'is-invalid': hasError('firstName') }"
                        trim
                        @input="filterThai('firstName')"
                      ></b-form-input>
                      <b-form-invalid-feedback>
                        กรุณากรอกชื่อเป็นภาษาไทย
                      </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group class="col-md-6">
                      <label>นามสกุล (ภาษาไทย)</label>
                      <b-form-input
                        v-model="form.lastName"
                        type="text"
                        placeholder="นามสกุล"
                        :class="{ 'is-invalid': hasError('lastName') }"
                        trim
                        @input="filterThai('lastName')"
                      ></b-form-input>
                      <b-form-invalid-feedback>
                        กรุณากรอกนามสกุลเป็นภาษาไทย
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </div>

                  <div class="form-row">
                    <b-form-group class="col-md-6">
                      <label>สถาบัน</label>
                      <b-form-select
                        v-model="form.organization"
                        :options="institutionOptions"
                      ></b-form-select>
                    </b-form-group>
                  </div>

                  <b-form-group label="ประวัติ" class="mb-3">
                    <b-form-textarea
                      v-model="form.about"
                      rows="5"
                      placeholder="ประวัติผู้สอน"
                    ></b-form-textarea>
                  </b-form-group>
                </div>
              </div>

              <!-- ข้อมูลผู้ใช้งาน -->
              <div class="card border-0 bg-light mb-3 no-shadow">
                <div class="card-header pt-3 border-0 bg-transparent">
                  <h5 class="card-title">ข้อมูลผู้ใช้งาน</h5>
                </div>
                <div class="card-body pb-0">
                  <div class="form-row">
                    <b-form-group class="col-md-6">
                      <label>อีเมล</label>
                      <b-form-input
                        v-model="form.email"
                        :class="{ 'is-invalid': hasError('email') }"
                        type="email"
                        placeholder="example@email.com"
                        trim
                      ></b-form-input>
                      <b-form-invalid-feedback>
                        กรุณากรอกอีเมลให้ถูกต้อง หรือ มีอีเมลนี้ในระบบแล้ว
                      </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group class="col-md-6">
                      <label>ชื่อผู้ใช้งาน</label>
                      <b-form-input
                        v-model="form.username"
                        :class="{ 'is-invalid': hasError('username') }"
                        type="text"
                        placeholder="username"
                        trim
                        @change="checkUsername"
                      ></b-form-input>
                      <b-form-invalid-feedback>
                        ชื่อผู้ใช้งานไม่ถูกต้อง หรือ มีผู้ใช้งานนี้ในระบบอยู่แล้ว
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </div>

                  <change-password-form
                    :companyId="company.id"
                    :errors="errors"
                    :isSetPassword="isSetPassword"
                    :isSetConfirmPassword="isSetConfirmPassword"
                    @passwordConfirmed="setPassword"
                  ></change-password-form>
                </div>
              </div>

              <div class="m-t-35 text-right">
                <router-link
                  :to="{ name: 'instructor.index', params: { company: company.url } }"
                  class="btn btn-link text-csq"
                >
                  ยกเลิก
                </router-link>
                <button class="btn btn-dark" type="button" @click="handleSubmit">
                  บันทึก
                </button>
              </div>
            </b-form>
          </div>
        </div>
      </div>
      <!-- /.inner -->
    </b-container>

    <!-- Error modal: cannot create user account -->
    <b-modal
      v-model="showErrorModal"
      centered
      hide-header
      hide-footer
      body-class="text-center p-4"
    >
      <b-icon
        icon="x-circle"
        variant="danger"
        font-scale="3.5"
        class="my-3"
      ></b-icon>
      <h4 class="font-weight-bold">ไม่สามารถสร้างบัญชีผู้ใช้งานได้</h4>
      <div class="text-muted mb-3">
        <p v-for="(msg, i) in errorMessages" :key="i" class="mb-1">{{ msg }}</p>
      </div>
      <b-button variant="primary" class="px-4" @click="showErrorModal = false">
        ตกลง
      </b-button>
    </b-modal>

    <!-- Success modal: instructor added -->
    <b-modal
      v-model="showSuccessModal"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      body-class="text-center p-4"
    >
      <b-icon
        icon="check-circle"
        variant="success"
        font-scale="3.5"
        class="my-3"
      ></b-icon>
      <h4 class="font-weight-bold">เพิ่มผู้สอนสำเร็จ</h4>
      <p class="text-muted mb-3">ระบบได้ตั้งค่าหน้าที่ให้โดยอัตโนมัติเรียบร้อยแล้ว</p>
      <div class="d-inline-block text-left mb-3">
        <div>
          <span class="text-muted">ชื่อ - นามสกุล:</span>
          <strong class="ml-2">{{ createdName }}</strong>
        </div>
        <div>
          <span class="text-muted">หน้าที่:</span>
          <strong class="ml-2">ผู้สอน</strong>
        </div>
      </div>
      <div>
        <b-button variant="primary" class="px-4" @click="goToIndex">
          เสร็จสิ้น
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import api from "../../../services/api.js";
import ChangePasswordForm from "../member-management/shared/ChangePasswordForm.vue";
import PageHeader from "../../layouts/PageHeader";

export default {
  components: {
    ChangePasswordForm,
    PageHeader,
  },
  props: { company: { type: Object, default: null } },

  data() {
    return {
      form: {
        firstName: "",
        lastName: "",
        organization: null,
        about: "",
        email: null,
        username: "",
        password: "",
        passwordConfirm: "",
      },
      institutionOptions: [{ value: null, text: "เลือกสถาบัน" }],
      errors: [],
      errorMessages: [],
      createdName: "",
      isSetPassword: null,
      isSetConfirmPassword: null,
      showErrorModal: false,
      showSuccessModal: false,
    };
  },

  created() {
    // Institution options default to the current company/brand (stored in mp_company).
    const c = this.company || {};
    const info = this.$store.state.Company.companyInfo || {};
    const companyName =
      c.displayName || c.name || info.displayName || info.name;
    if (companyName) {
      this.institutionOptions = [
        { value: null, text: "เลือกสถาบัน" },
        { value: companyName, text: companyName },
      ];
      this.form.organization = companyName;
    }
  },

  methods: {
    addInstructor(form) {
      api
        .post(`/v1/instructors`, {
          company_id: this.company.id,
          first_name: form.firstName,
          last_name: form.lastName,
          organization: form.organization,
          about: form.about,
          email: form.email,
          username: form.username,
          password: form.password,
        })
        .then(() => {
          this.createdName = `${form.firstName} ${form.lastName}`.trim();
          this.showSuccessModal = true;
        })
        .catch(({ response }) => {
          const data = (response && response.data) || {};
          const errs = data.errors || {};

          if (errs.email) this.errors.push("email");
          if (errs.username) this.errors.push("username");

          // Show the specific field messages (email/username already taken, etc.);
          // fall back to the top-level message only when there are no field errors.
          const messages = Object.keys(errs).reduce(
            (acc, key) => acc.concat(errs[key]),
            []
          );
          this.errorMessages = messages.length
            ? messages
            : [data.message || "กรุณาตรวจสอบข้อมูลอีกครั้ง"];

          this.showErrorModal = true;
        });
    },

    async checkUsername(username) {
      const idx = this.errors.indexOf("username");
      if (idx !== -1) this.errors.splice(idx, 1);
      if (username) {
        const { data } = await api.get(`/v1/check-username/${username}`);
        if (!data) this.errors.push("username");
      }
    },

    setPassword(data) {
      this.form.password = data.password;
      this.form.passwordConfirm = data.passwordConfirm;
    },

    handleSubmit() {
      if (this.validateForm()) {
        this.addInstructor(this.form);
      }
    },

    filterThai(field) {
      this.form[field] = this.form[field].replace(/[^ก-๙\s.-]/g, '');
    },

    isThaiOnly(value) {
      return /^[ก-๙\s.-]+$/.test(value);
    },

    validateForm() {
      this.errors = [];

      const form = this.form;
      if (!form.firstName || !this.isThaiOnly(form.firstName)) this.errors.push("firstName");
      if (!form.lastName || !this.isThaiOnly(form.lastName)) this.errors.push("lastName");
      if (form.email && !this.validEmail(form.email)) this.errors.push("email");
      if (!form.email && !form.username) {
        this.errors.push("email");
        this.errors.push("username");
      }
      if (!form.password) {
        this.errors.push("confirmPassword");
        this.isSetPassword = false;
      }
      if (form.password && !form.passwordConfirm) {
        this.errors.push("confirmPassword");
        this.isSetConfirmPassword = false;
      }

      return !this.errors.length;
    },

    validEmail(email) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(email);
    },

    hasError(field) {
      return this.errors.includes(field);
    },

    goToIndex() {
      this.showSuccessModal = false;
      this.$router.push({
        name: "instructor.index",
        params: { company: this.company.url },
      });
    },
  },
};
</script>

